/**
 * Silent sign-in for a tab that has no account (ADR-0075).
 *
 * MSAL's cache is per tab (ADR-0053), so a tab opened from a signed-in tab
 * starts with no account. Before showing the login page, `RequireAuth` asks
 * Microsoft for a sign-in with `prompt=none` through a top-level redirect: no
 * screen when the Microsoft session is there, and no dependence on
 * third-party cookies (the reason ADR-0053 turned `ssoSilent` down). The page
 * that was opened travels as MSAL `state` and the tab returns to it.
 *
 * What this module remembers lives in page memory only — never in storage —
 * and that is enough, because a redirect ends in a fresh page load:
 *
 * - `refused`: Microsoft answered the redirect with an error (no session,
 *   interaction required, several accounts without a hint, policy). The tab
 *   shows the login page and does not try again by itself, so there is no
 *   loop. msal-react swallows the error of `handleRedirectPromise`; it is
 *   seen here through MSAL's `LOGIN_FAILURE` event, registered on the
 *   instance before the provider starts handling the redirect.
 * - `returnTarget`: the `state` of a successful redirect, validated by the
 *   same rule as a sign-in return (`returnPath`, ADR-0070). When the redirect
 *   fails the target is lost with it and the login page returns to Home.
 * - `started`: one attempt per page load.
 */

import {
  EventType,
  InteractionType,
  type AuthenticationResult,
  type EventMessage,
  type IPublicClientApplication,
  type RedirectRequest,
} from "@azure/msal-browser";
import { returnPath } from "./returnTo";
import { askOtherTabsForAccount } from "./tabs";

let refused = false;
let started = false;
let returnTarget: string | null = null;

/** MSAL event callback: registered on the instance in `MsalBootstrap`. */
export function noteMsalEvent(message: EventMessage): void {
  if (message.interactionType !== InteractionType.Redirect) return;
  if (
    message.eventType === EventType.LOGIN_SUCCESS ||
    message.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
  ) {
    const state = (message.payload as AuthenticationResult | null)?.state;
    if (state) returnTarget = returnPath({ from: state });
  } else if (
    message.eventType === EventType.LOGIN_FAILURE ||
    message.eventType === EventType.ACQUIRE_TOKEN_FAILURE
  ) {
    refused = true;
  }
}

/** The silent attempt of this page load was refused, or could not be made. */
export function silentSignInRefused(): boolean {
  return refused;
}

/** A silent attempt has been started in this page load (the tab is leaving). */
export function silentSignInStarted(): boolean {
  return started;
}

/** The page a successful silent redirect asked for, if it is still pending. */
export function peekReturnTarget(): string | null {
  return returnTarget;
}

/** Called once that page is reached. Idempotent. */
export function clearReturnTarget(): void {
  returnTarget = null;
}

/**
 * Start the silent redirect. Resolves without navigating only when the
 * request itself could not be made (then `silentSignInRefused()` is true).
 */
export async function startSilentSignIn(
  instance: IPublicClientApplication,
  scopes: string[],
  target: string,
): Promise<void> {
  if (started || refused) return;
  started = true;
  const hint = await askOtherTabsForAccount();
  const request: RedirectRequest = { scopes, prompt: "none", state: target };
  if (hint) request.loginHint = hint;
  try {
    await instance.loginRedirect(request);
  } catch {
    refused = true;
  }
}

/** Test hooks. */
export function _resetSilentSignInForTests(): void {
  refused = false;
  started = false;
  returnTarget = null;
}
export function _markSilentSignInRefusedForTests(): void {
  refused = true;
}
export function _setReturnTargetForTests(target: string | null): void {
  returnTarget = target;
}
